import { RangeSetBuilder } from "@codemirror/state";
import { Decoration, ViewPlugin } from "@codemirror/view";

import type { DecorationSet, EditorView, ViewUpdate } from "@codemirror/view";

/** Applied to every `.cm-line` whose text (ignoring leading whitespace) starts with `//`. */
const COMMENT_LINE_CLASS = "cm-json-comment-line";
const commentLineMark = Decoration.line({ class: COMMENT_LINE_CLASS });

function buildCommentLineDecorations(view: EditorView): DecorationSet {
    const builder = new RangeSetBuilder<Decoration>();
    for (const { from, to } of view.visibleRanges) {
        let pos = from;
        while (pos <= to) {
            const line = view.state.doc.lineAt(pos);
            if (line.text.trimStart().startsWith("//")) {
                builder.add(line.from, line.from, commentLineMark);
            }
            pos = line.to + 1;
        }
    }
    return builder.finish();
}

/**
 * Visually distinguishes `//` comment lines (toggled via {@link toggleJsonLineComment})
 * by tagging their `.cm-line` element with `.cm-json-comment-line`, which
 * `RVJsonEditor.module.scss` styles as greyed-out and italic — otherwise a
 * commented-out line looks identical to normal (syntax-highlighted) JSON,
 * which defeats the point of commenting it out.
 */
export const jsonCommentHighlight = ViewPlugin.fromClass(
    class {
        decorations: DecorationSet;

        constructor(view: EditorView) {
            this.decorations = buildCommentLineDecorations(view);
        }

        update(update: ViewUpdate): void {
            if (update.docChanged || update.viewportChanged) {
                this.decorations = buildCommentLineDecorations(update.view);
            }
        }
    },
    { decorations: (plugin) => plugin.decorations }
);
